import { useState, useImperativeHandle, forwardRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { projectService } from '@/services/projectService'
import { userService, type User } from '@/services/userService'
import { useAuth } from '@/hooks/useAuth'

interface FormData {
  name: string
  description: string
  owner_id: string
}

interface FormErrors {
  name: string
  description: string
  owner_id: string
}

interface AddProjectModalProps {
  currentUserRole: 'owner' | 'admin' | 'auditor' | 'security' | 'user'
  onSuccess?: () => void
}

export interface AddProjectModalRef {
  open: () => void
  close: () => void
}

const INITIAL_FORM_DATA: FormData = {
  name: '',
  description: '',
  owner_id: '',
}

const INITIAL_FORM_ERRORS: FormErrors = {
  name: '',
  description: '',
  owner_id: '',
}

const AddProjectModal = forwardRef<AddProjectModalRef, AddProjectModalProps>(
  ({ currentUserRole, onSuccess }, ref) => {
    const { t } = useTranslation('projects')
    const { user: currentUser } = useAuth()
    const [isOpen, setIsOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState(false)
    const [actionType, setActionType] = useState<'direct' | 'request'>('direct')
    const [users, setUsers] = useState<User[]>([])
    const [loadingUsers, setLoadingUsers] = useState(false)

    const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA)
    const [formErrors, setFormErrors] = useState<FormErrors>(INITIAL_FORM_ERRORS)

    // User selector state
    const [showUserSelector, setShowUserSelector] = useState(false)
    const [selectedUser, setSelectedUser] = useState<User | null>(null)
    const [userSearchQuery, setUserSearchQuery] = useState('')
    const [userPage, setUserPage] = useState(1)
    const [userTotalItems, setUserTotalItems] = useState(0)
    const userPageSize = 5

    const isOwner = currentUserRole === 'owner'

    // Fetch users for owner selection
    useEffect(() => {
      const fetchUsers = async () => {
        if (!showUserSelector) return

        try {
          setLoadingUsers(true)
          const offset = (userPage - 1) * userPageSize
          const response = await userService.getUsers({
            limit: userPageSize,
            offset,
            q: userSearchQuery || undefined
          })
          setUsers(response.users || [])
          setUserTotalItems(response.total || 0)
        } catch (err) {
          console.error('Failed to fetch users:', err)
        } finally {
          setLoadingUsers(false)
        }
      }

      fetchUsers()
    }, [showUserSelector, userPage, userSearchQuery])

    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          setIsOpen(true)
          setError('')
          setSuccess(false)
          setActionType(isOwner ? 'direct' : 'request')
          setFormData({
            ...INITIAL_FORM_DATA,
            owner_id: currentUser?.id || '',
          })
          setFormErrors({ ...INITIAL_FORM_ERRORS })
          setSelectedUser(currentUser as User | null)
          setShowUserSelector(false)
          setUserSearchQuery('')
          setUserPage(1)
        },
        close: () => {
          setIsOpen(false)
          setSuccess(false)
          setActionType('direct')
          setShowUserSelector(false)
        },
      }),
      [isOwner, currentUser]
    )

    const validateForm = (): boolean => {
      const errors: FormErrors = { ...INITIAL_FORM_ERRORS }

      if (!formData.name.trim()) {
        errors.name = t('addModal.errors.nameRequired', 'Project name is required')
      }

      if (!formData.description.trim()) {
        errors.description = t('addModal.errors.descriptionRequired', 'Description is required')
      }

      if (!formData.owner_id) {
        errors.owner_id = t('addModal.errors.ownerRequired', 'Owner is required')
      }

      setFormErrors(errors)
      return !Object.values(errors).some((error) => error !== '')
    }

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()

      if (!validateForm()) {
        return
      }

      try {
        setLoading(true)
        setError('')

        const projectData = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          owner_id: formData.owner_id,
        }

        if (actionType === 'direct') {
          // Owner directly creates project
          await projectService.createProject(projectData)
        } else {
          // Submit request (Admin or Owner chooses request method)
          await projectService.requestProjectCreate(projectData)
        }

        setSuccess(true)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
        if (errorMessage.startsWith('errors.')) {
          setError(t(errorMessage, errorMessage))
        } else {
          setError(errorMessage)
        }
      } finally {
        setLoading(false)
      }
    }

    const handleClose = () => {
      const wasSuccess = success
      setIsOpen(false)
      setFormData({ ...INITIAL_FORM_DATA })
      setFormErrors({ ...INITIAL_FORM_ERRORS })
      setError('')
      setSuccess(false)
      setActionType('direct')
      setShowUserSelector(false)
      setSelectedUser(null)

      if (wasSuccess) {
        onSuccess?.()
      }
    }

    const handleChange = <K extends keyof FormData>(field: K, value: FormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }))
      if (field in formErrors) {
        setFormErrors((prev) => ({ ...prev, [field as keyof FormErrors]: '' }))
      }
    }

    const handleSelectUser = (user: User) => {
      setSelectedUser(user)
      setFormData((prev) => ({ ...prev, owner_id: user.id }))
      setFormErrors((prev) => ({ ...prev, owner_id: '' }))
      setShowUserSelector(false)
      setUserSearchQuery('')
      setUserPage(1)
    }

    const handleUserSearchChange = (query: string) => {
      setUserSearchQuery(query)
      setUserPage(1) // Reset to first page when searching
    }

    if (!isOpen) return null

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-primary/5 to-transparent">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('addModal.title', 'Add Project')}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t('addModal.subtitle', 'Create a new project')}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="ml-4 text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
                disabled={loading}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-5">
              {/* Success Message */}
              {success && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <svg
                      className="w-5 h-5 text-green-600 mt-0.5 shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-green-900 mb-1">
                        {actionType === 'direct'
                          ? t('addModal.successDirect', 'Project created successfully!')
                          : t('addModal.successRequest', 'Project creation request submitted successfully!')}
                      </p>
                      <p className="text-xs text-green-700">
                        {actionType === 'direct'
                          ? t('addModal.successDirectDesc', 'The project has been created immediately.')
                          : t(
                              'addModal.successRequestDesc',
                              'The request is pending owner approval.'
                            )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && !success && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2.5">
                  <svg
                    className="w-5 h-5 text-red-600 mt-0.5 shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm text-red-700 flex-1">{error}</p>
                </div>
              )}

              {/* Project Name Field */}
              {!success && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('addModal.name', 'Project Name')}
                    <span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder={t('addModal.namePlaceholder', 'Enter project name')}
                    className={`w-full px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                      formErrors.name
                        ? 'border-red-500 focus:ring-red-200'
                        : 'border-gray-300 focus:ring-primary/30'
                    }`}
                    disabled={loading}
                  />
                  {formErrors.name && (
                    <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {formErrors.name}
                    </p>
                  )}
                </div>
              )}

              {/* Description Field */}
              {!success && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('addModal.description', 'Description')}
                    <span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder={t('addModal.descriptionPlaceholder', 'Enter project description')}
                    rows={3}
                    className={`w-full px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all resize-none ${
                      formErrors.description
                        ? 'border-red-500 focus:ring-red-200'
                        : 'border-gray-300 focus:ring-primary/30'
                    }`}
                    disabled={loading}
                  />
                  {formErrors.description && (
                    <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {formErrors.description}
                    </p>
                  )}
                </div>
              )}

              {/* Owner Selection Field */}
              {!success && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('addModal.owner', 'Project Owner')}
                    <span className="text-red-500 ml-0.5">*</span>
                  </label>

                  {/* Selected User Display */}
                  {selectedUser && !showUserSelector ? (
                    <div
                      className={`w-full px-3.5 py-2.5 border rounded-lg bg-gray-50 ${
                        formErrors.owner_id ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0">
                            {selectedUser.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {selectedUser.name}
                            </div>
                            <div className="text-xs text-gray-500">@{selectedUser.account}</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowUserSelector(true)}
                          className="text-sm text-primary hover:text-primary-dark font-medium"
                          disabled={loading}
                        >
                          {t('addModal.changeOwner', 'Change')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowUserSelector(true)}
                      className={`w-full px-3.5 py-2.5 border rounded-lg bg-white text-left transition-all ${
                        formErrors.owner_id
                          ? 'border-red-500 hover:border-red-600'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      disabled={loading}
                    >
                      <span className="text-sm text-gray-500">
                        {t('addModal.selectOwner', 'Click to select project owner')}
                      </span>
                    </button>
                  )}

                  {formErrors.owner_id && (
                    <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {formErrors.owner_id}
                    </p>
                  )}

                  {/* User Selector Table */}
                  {showUserSelector && (
                    <div className="mt-3 border border-gray-300 rounded-lg overflow-hidden">
                      {/* Search Bar */}
                      <div className="p-3 bg-gray-50 border-b border-gray-200">
                        <div className="relative">
                          <input
                            type="text"
                            value={userSearchQuery}
                            onChange={(e) => handleUserSearchChange(e.target.value)}
                            placeholder={t('addModal.searchUsers', 'Search users...')}
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                          />
                          <svg
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* User Table */}
                      <div className="max-h-64 overflow-y-auto">
                        {loadingUsers ? (
                          <div className="p-8 text-center">
                            <div className="inline-block w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-sm text-gray-500 mt-2">
                              {t('addModal.loadingUsers', 'Loading users...')}
                            </p>
                          </div>
                        ) : users.length === 0 ? (
                          <div className="p-8 text-center">
                            <svg
                              className="w-12 h-12 text-gray-300 mx-auto mb-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                              />
                            </svg>
                            <p className="text-sm text-gray-500">
                              {t('addModal.noUsersFound', 'No users found')}
                            </p>
                          </div>
                        ) : (
                          <table className="w-full">
                            <tbody className="divide-y divide-gray-200">
                              {users.map((user) => (
                                <tr
                                  key={user.id}
                                  onClick={() => handleSelectUser(user)}
                                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                                >
                                  <td className="px-3 py-3">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0">
                                        {user.name.charAt(0).toUpperCase()}
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-gray-900 truncate">
                                          {user.name}
                                        </div>
                                        <div className="text-xs text-gray-500 truncate">
                                          @{user.account}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-3 py-3 text-right">
                                    <span className="text-xs text-gray-400">
                                      {t(`roles.${user.platform_role}`, user.platform_role)}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>

                      {/* Pagination */}
                      {!loadingUsers && users.length > 0 && (
                        <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs">
                          <button
                            type="button"
                            onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                            disabled={userPage === 1}
                            className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {t('addModal.previous', 'Previous')}
                          </button>
                          <span className="text-gray-600">
                            {t('addModal.pageInfo', 'Page {{current}} of {{total}}', {
                              current: userPage,
                              total: Math.max(1, Math.ceil(userTotalItems / userPageSize)),
                            })}
                          </span>
                          <button
                            type="button"
                            onClick={() => setUserPage((p) => p + 1)}
                            disabled={userPage >= Math.ceil(userTotalItems / userPageSize)}
                            className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {t('addModal.next', 'Next')}
                          </button>
                        </div>
                      )}

                      {/* Close Button */}
                      <div className="px-3 py-2 bg-gray-100 border-t border-gray-200 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setShowUserSelector(false)}
                          className="text-xs text-gray-600 hover:text-gray-800 font-medium"
                        >
                          {t('addModal.closeSelector', 'Close')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Action Type Selection (Only for Owner) */}
              {!success && isOwner && (
                <div className="pt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t('addModal.actionType', 'Action Type')}
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="cursor-pointer">
                      <input
                        type="radio"
                        name="actionType"
                        value="direct"
                        checked={actionType === 'direct'}
                        onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                        disabled={loading}
                        className="sr-only"
                      />
                      <div
                        className={`h-full px-4 py-3 border-2 rounded-lg transition-all ${
                          actionType === 'direct'
                            ? 'border-primary bg-primary/10'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <span className="text-sm font-medium text-gray-900 block">
                          {t('addModal.directCreate', 'Direct Create')}
                        </span>
                        <p className="text-xs text-gray-600 mt-1">
                          {t('addModal.directCreateDesc', 'Immediately create the project')}
                        </p>
                      </div>
                    </label>
                    <label className="cursor-pointer">
                      <input
                        type="radio"
                        name="actionType"
                        value="request"
                        checked={actionType === 'request'}
                        onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                        disabled={loading}
                        className="sr-only"
                      />
                      <div
                        className={`h-full px-4 py-3 border-2 rounded-lg transition-all ${
                          actionType === 'request'
                            ? 'border-primary bg-primary/10'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <span className="text-sm font-medium text-gray-900 block">
                          {t('addModal.submitRequest', 'Submit Request')}
                        </span>
                        <p className="text-xs text-gray-600 mt-1">
                          {t('addModal.submitRequestDesc', 'Create a project creation request')}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Admin Info Notice */}
              {!success && !isOwner && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-2">
                  <div className="flex items-start gap-3">
                    <svg
                      className="w-5 h-5 text-blue-600 mt-0.5 shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm text-blue-700">
                        {t(
                          'addModal.adminNotice',
                          'As an admin, you can submit a project creation request. The request will need to be approved by an owner.'
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="border-t border-gray-100 px-6 py-4 bg-gray-50/50 flex items-center justify-end gap-3">
              {success ? (
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  {t('addModal.close', 'Close')}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-all font-medium"
                    disabled={loading}
                  >
                    {t('addModal.cancel', 'Cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-sm hover:shadow"
                    disabled={loading}
                  >
                    {loading && (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    )}
                    <span>
                      {loading
                        ? t('addModal.processing', 'Processing')
                        : actionType === 'direct'
                          ? t('addModal.create', 'Create Project')
                          : t('addModal.submitRequest', 'Submit Request')}
                    </span>
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    )
  }
)

AddProjectModal.displayName = 'AddProjectModal'

export default AddProjectModal
